import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Mail, Flag, Save, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiGet, apiPut } from '@/lib/api/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { AdminErrorState, AdminLoadingState } from './index';

interface PlatformSetting {
  id: string;
  key: string;
  value: string;
  category: string;
  description: string;
  isPublic: boolean;
  updatedAt: string;
  createdAt: string;
}

interface ConfigResponse {
  settings: PlatformSetting[];
}

// Default settings that should exist in the database
const defaultSettings = {
  email: {
    'email.from_name': 'securesight.ai Platform',
    'email.from_address': 'noreply@securesight.ai.com',
    'email.reply_to': 'support@securesight.ai.com',
  },
  features: {
    'platform.registration_enabled': 'true',
    'platform.email_verification_required': 'true',
    'platform.maintenance_mode': 'false',
    'content.max_file_size_mb': '50',
    'content.allowed_file_types': 'pdf,pptx,png,jpg,jpeg,mp4,md',
  },
  general: {
    'platform.name': 'securesight.ai',
    'platform.tagline': 'AI Consulting Excellence',
    'platform.admin_email': 'admin@securesight.ai.com',
  },
};

export function SystemConfiguration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localSettings, setLocalSettings] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { error: authError, checkAuth } = useAdminAuth();

  const fetchSettings = async () => {
    try {
      setError(null);

      // Validate authentication before making request
      const authValid = await checkAuth();
      if (!authValid) {
        setError(authError || 'Authentication required. Please log in.');
        return;
      }

      // Use centralized API client
      const data: ConfigResponse = await apiGet<ConfigResponse>('/api/admin/config');

      // Create a map of key -> value for easy access
      const settingsMap: Record<string, string> = {};
      data.settings.forEach((setting) => {
        settingsMap[setting.key] = setting.value;
      });
      setLocalSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load platform settings';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSettings();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate authentication before making request
      const authValid = await checkAuth();
      if (!authValid) {
        throw new Error(authError || 'Authentication required. Please log in.');
      }

      // Convert localSettings back to array format
      const settingsArray = Object.entries(localSettings).map(([key, value]) => ({
        key,
        value,
      }));

      // Use centralized API client
      await apiPut('/api/admin/config', { settings: settingsArray });

      toast({
        title: 'Success',
        description: 'Platform settings saved successfully',
      });

      // Refresh settings from server
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save platform settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: string, value: string) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getSettingValue = (key: string, defaultValue: string = ''): string => {
    return localSettings[key] || defaultValue;
  };

  const getBooleanSetting = (key: string): boolean => {
    return getSettingValue(key, 'false') === 'true';
  };

  const setBooleanSetting = (key: string, value: boolean) => {
    handleSettingChange(key, value ? 'true' : 'false');
  };

  if (loading) {
    return (
      <AdminLoadingState
        title="System Configuration"
        description="Loading platform settings..."
        skeletonCount={4}
      />
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <AdminErrorState
        title="System Configuration"
        error={error}
        onRetry={fetchSettings}
        icon={<Settings className="h-5 w-5 text-destructive" />}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>System Configuration</CardTitle>
            <CardDescription>Manage platform-wide settings and feature flags</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2">
                <Settings className="h-5 w-5" />
                <h3 className="text-lg font-semibold">General Settings</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform.name">Platform Name</Label>
                <Input
                  id="platform.name"
                  value={getSettingValue('platform.name', defaultSettings.general['platform.name'])}
                  onChange={(e) => handleSettingChange('platform.name', e.target.value)}
                  placeholder="securesight.ai"
                />
                <p className="text-sm text-muted-foreground">
                  The name of your platform displayed to users
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform.tagline">Platform Tagline</Label>
                <Input
                  id="platform.tagline"
                  value={getSettingValue('platform.tagline', defaultSettings.general['platform.tagline'])}
                  onChange={(e) => handleSettingChange('platform.tagline', e.target.value)}
                  placeholder="AI Consulting Excellence"
                />
                <p className="text-sm text-muted-foreground">
                  A short tagline describing your platform
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="platform.admin_email">Admin Email</Label>
                <Input
                  id="platform.admin_email"
                  type="email"
                  value={getSettingValue('platform.admin_email', defaultSettings.general['platform.admin_email'])}
                  onChange={(e) => handleSettingChange('platform.admin_email', e.target.value)}
                  placeholder="admin@securesight.ai.com"
                />
                <p className="text-sm text-muted-foreground">
                  Contact email for platform administration
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2">
                <Mail className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Email Configuration</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email.from_name">From Name</Label>
                <Input
                  id="email.from_name"
                  value={getSettingValue('email.from_name', defaultSettings.email['email.from_name'])}
                  onChange={(e) => handleSettingChange('email.from_name', e.target.value)}
                  placeholder="securesight.ai Platform"
                />
                <p className="text-sm text-muted-foreground">
                  Display name for outgoing emails
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email.from_address">From Address</Label>
                <Input
                  id="email.from_address"
                  type="email"
                  value={getSettingValue('email.from_address', defaultSettings.email['email.from_address'])}
                  onChange={(e) => handleSettingChange('email.from_address', e.target.value)}
                  placeholder="noreply@securesight.ai.com"
                />
                <p className="text-sm text-muted-foreground">
                  Sender email address for system emails
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email.reply_to">Reply-To Address</Label>
                <Input
                  id="email.reply_to"
                  type="email"
                  value={getSettingValue('email.reply_to', defaultSettings.email['email.reply_to'])}
                  onChange={(e) => handleSettingChange('email.reply_to', e.target.value)}
                  placeholder="support@securesight.ai.com"
                />
                <p className="text-sm text-muted-foreground">
                  Email address for replies to system emails
                </p>
              </div>

              <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4 border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> SMTP credentials and API keys are configured via environment variables for security.
                  These settings control only the display names and addresses used in emails.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Feature Flags */}
          <TabsContent value="features" className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2">
                <Flag className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Feature Flags</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="platform.registration_enabled">User Registration</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow new users to register on the platform
                    </p>
                  </div>
                  <Switch
                    id="platform.registration_enabled"
                    checked={getBooleanSetting('platform.registration_enabled')}
                    onCheckedChange={(checked) => setBooleanSetting('platform.registration_enabled', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="platform.email_verification_required">Email Verification Required</Label>
                    <p className="text-sm text-muted-foreground">
                      Require email verification for new user accounts
                    </p>
                  </div>
                  <Switch
                    id="platform.email_verification_required"
                    checked={getBooleanSetting('platform.email_verification_required')}
                    onCheckedChange={(checked) => setBooleanSetting('platform.email_verification_required', checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="platform.maintenance_mode">Maintenance Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Temporarily disable access for non-admin users
                    </p>
                  </div>
                  <Switch
                    id="platform.maintenance_mode"
                    checked={getBooleanSetting('platform.maintenance_mode')}
                    onCheckedChange={(checked) => setBooleanSetting('platform.maintenance_mode', checked)}
                  />
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label htmlFor="content.max_file_size_mb">Max File Size (MB)</Label>
                <Input
                  id="content.max_file_size_mb"
                  type="number"
                  value={getSettingValue('content.max_file_size_mb', defaultSettings.features['content.max_file_size_mb'])}
                  onChange={(e) => handleSettingChange('content.max_file_size_mb', e.target.value)}
                  placeholder="50"
                />
                <p className="text-sm text-muted-foreground">
                  Maximum file size for uploads in megabytes
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content.allowed_file_types">Allowed File Types</Label>
                <Textarea
                  id="content.allowed_file_types"
                  value={getSettingValue('content.allowed_file_types', defaultSettings.features['content.allowed_file_types'])}
                  onChange={(e) => handleSettingChange('content.allowed_file_types', e.target.value)}
                  placeholder="pdf,pptx,png,jpg,jpeg,mp4,md"
                  rows={3}
                />
                <p className="text-sm text-muted-foreground">
                  Comma-separated list of allowed file extensions (without dots)
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-6 border-t">
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
